import { useMemo } from 'react';
import { useMediaQuery } from '@mui/material';

import BudgetItemsTable, { IBudgetItem } from './BudgetItemsTable';
import BudgetObservation from './BudgetObservation';
import BudgetServiceFields from './BudgetServiceFields';
import { parseDiscountPercent } from '../utils/budgetCalculations';
import { IVehicleData } from '../types';
import { ITruckerPayPlanData } from '../../../../models/truckerPayPlan';

interface IBudgetValuesSectionProps {
  serviceId: string;
  failureModeId?: string;
  items?: IBudgetItem[];
  proposalItems?: IBudgetItem[];
  observation?: string;
  clientPercentage?: number;
  volkstotalPercentage?: number;
  dealershipPercentage?: number;
  warrantyPercentage?: number;
  isExternalService?: boolean;
  isEditMode?: boolean;
  showPercentageFields?: boolean;
  vehicleData?: IVehicleData;
  serviceType: string;
  truckerPayPlan?: ITruckerPayPlanData;
  onServiceChange?: (serviceId: string, serviceData?: any) => void;
  onFailureModeChange?: (failureModeId: string) => void;
  onItemChange?: (index: number, field: keyof IBudgetItem, value: string | number) => void;
  onRemoveItem?: (index: number) => void;
  onAddItem?: () => void;
  onAddObservation?: () => void;
  onObservationChange?: (observation: string) => void;
  onRemoveObservation?: () => void;
  onRemoveService?: () => void;
  onClientPercentageChange?: (value: number) => void;
  onVolkstotalPercentageChange?: (value: number) => void;
  onDealershipPercentageChange?: (value: number) => void;
  onWarrantyPercentageChange?: (value: number) => void;
  onChangeServiceType?: (serviceType: string) => void;
}

const parseFromNumericValue = (value: number | string): number => {
  if (typeof value === 'number') {
    return value;
  }

  if (!value) {
    return 0;
  }

  const cleanValue = value.toString().replaceAll(/[^\d.]/g, '');
  const parts = cleanValue.split('.');

  if (parts.length === 1) {
    return Number.parseFloat(parts[0] || '0');
  }

  const reais = Number.parseFloat(parts[0] || '0');
  const centavos = Number.parseFloat((parts[1] || '00').padEnd(2, '0').substring(0, 2)) / 100;

  return reais + centavos;
};

const formatToNumericValue = (value: number): string => {
  const reais = Math.floor(value);
  const centavos = Math.round((value - reais) * 100);
  return `${reais}.${String(centavos).padStart(2, '0')}`;
};

const formatCurrencyDisplay = (value: string): string => {
  const numericValue = parseFromNumericValue(value);

  if (numericValue === 0) {
    return 'R$ 00,00';
  }

  const reais = Math.floor(numericValue);
  const centavos = Math.round((numericValue - reais) * 100);

  const formattedReais = reais.toLocaleString('pt-BR');
  const formattedCentavos = String(centavos).padStart(2, '0');

  return `R$ ${formattedReais},${formattedCentavos}`;
};

const BudgetValuesSection = (props: IBudgetValuesSectionProps) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const items = props.items || [];
  const observation = props.observation;

  const calculatedSubtotal = useMemo(() => {
    if (items.length === 0) {
      return 'R$ 00,00';
    }

    const total = items.reduce((sum, item) => {
      const itemValue = item.isCovered || !item.value ? 0 : item.value;
      let clean = String(item.quantity || '0').replace(/[^\d.,]/g, '');
      clean = clean.replace(',', '.');
      const quantityParsed = Number.parseFloat(clean);
      const quantity = Number.isNaN(quantityParsed) ? 0 : quantityParsed;
      return sum + (itemValue * quantity * (1 - parseDiscountPercent(item.discount) / 100));
    }, 0);

    return formatCurrencyDisplay(formatToNumericValue(total));
  }, [items]);

  return (
    <div className={`border border-gray-200 dark:border-neutral-700 rounded-md ${isMobile ? 'p-3 my-4 ' : 'py-4 mt-3 my-6'} my-0`}>
      <div className="mb-4 p-4">
        <BudgetServiceFields
          serviceId={props.serviceId}
          failureModeId={props.failureModeId}
          isExternalService={props.isExternalService}
          volkstotalPercentage={props.volkstotalPercentage}
          dealershipPercentage={props.dealershipPercentage}
          warrantyPercentage={props.warrantyPercentage}
          showPercentageFields={props.showPercentageFields}
          vehicleData={props.vehicleData}
          serviceType={props.serviceType}
          truckerPayPlan={props.truckerPayPlan}
          onServiceChange={props.onServiceChange}
          onFailureModeChange={props.onFailureModeChange}
          onVolkstotalPercentageChange={props.onVolkstotalPercentageChange}
          onDealershipPercentageChange={props.onDealershipPercentageChange}
          onWarrantyPercentageChange={props.onWarrantyPercentageChange}
          onRemoveService={props.onRemoveService}
          onChangeServiceType={props.onChangeServiceType}
        />
      </div>
      <BudgetItemsTable
        items={items}
        proposalItems={props.proposalItems}
        isExternalService={props.isExternalService}
        isEditMode={props.isEditMode}
        onItemChange={props.onItemChange}
        onRemoveItem={props.onRemoveItem}
      />
      {observation !== undefined && (
        <div className="mt-4">
          <BudgetObservation
            observation={observation}
            onObservationChange={props.onObservationChange}
            onRemoveObservation={props.onRemoveObservation}
          />
        </div>
      )}
      <div className={`flex ${isMobile ? 'flex-col gap-3' : 'flex-row justify-between items-center'} mt-4 ${isMobile ? 'pl-0 pr-0' : 'pl-4 pr-6'}`}>
        <div className={`flex ${isMobile ? 'flex-col gap-2' : 'flex-row gap-4'}`}>
          {props.onAddItem && (
            <button
              type="button"
              className={`flex flex-row items-center text-gray-500 dark:text-neutral-400 hover:text-gray-700 dark:hover:text-neutral-300 bg-transparent border-none p-0 cursor-pointer ${isMobile ? '' : 'mr-4'}`}
              onClick={props.onAddItem}
            >
              <img
                src="/assets/images/plus.svg"
                alt="Adicionar item"
                className="w-5 h-5 flex-shrink-0 mr-2 dark:invert"
              />
              <span>Adicionar item</span>
            </button>
          )}
          {observation === undefined && (
            <button
              type="button"
              className="flex flex-row items-center text-gray-500 dark:text-neutral-400 hover:text-gray-700 dark:hover:text-neutral-300 bg-transparent border-none p-0 cursor-pointer"
              onClick={props.onAddObservation}
            >
              <img
                src="/assets/images/clipboard.svg"
                alt="Adicionar observação"
                className="w-5 h-5 flex-shrink-0 mr-2 dark:invert"
              />
              <span>Adicionar observação</span>
            </button>
          )}
        </div>
        <div className={`${isMobile ? 'text-size-14' : 'text-size-18'} text-gray-500 dark:text-neutral-400`}>Subtotal do serviço: {calculatedSubtotal}</div>
      </div>
    </div>
  );
};

export default BudgetValuesSection;
